import { api, handleApiError } from '../client'
import { buildODataQuery, type ODataQueryParams } from '../odata'

export interface RoleOData {
  id: string
  name: string
  description?: string
  permissions?: string[]
  userCount?: number
  isSystemRole: boolean
  isDefault: boolean
  createdAt: string
}

export interface RolesODataResponse {
  value: RoleOData[]
  '@odata.count'?: number
}

/**
 * Normalizes an object's keys from PascalCase to camelCase.
 * OData responses use PascalCase, but frontend expects camelCase.
 */
function normalizeKeys<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {}
  for (const key in obj) {
    const camelKey = key.charAt(0).toLowerCase() + key.slice(1)
    const value = obj[key]
    // Recursively normalize nested objects and arrays
    if (Array.isArray(value)) {
      result[camelKey] = value.map(item =>
        typeof item === 'object' && item !== null
          ? normalizeKeys(item as Record<string, unknown>)
          : item
      )
    } else if (typeof value === 'object' && value !== null) {
      result[camelKey] = normalizeKeys(value as Record<string, unknown>)
    } else {
      result[camelKey] = value
    }
  }
  return result as T
}

export const roleService = {
  /**
   * Gets roles with OData query support.
   * Supports filtering, sorting, paging via OData parameters.
   */
  async getRoles(params?: ODataQueryParams): Promise<RolesODataResponse> {
    try {
      const queryString = params ? buildODataQuery(params) : ''
      const response = await api.get<RolesODataResponse | RoleOData[]>(`/v1/roles${queryString}`)

      // Handle both OData response and array response for backwards compatibility
      if (Array.isArray(response.data)) {
        return {
          value: response.data.map(item => normalizeKeys<RoleOData>(item as unknown as Record<string, unknown>)),
          '@odata.count': response.data.length
        }
      }

      // Normalize OData response - keys are PascalCase from OData
      const data = response.data as { value?: unknown[]; '@odata.count'?: number }
      return {
        value: (data.value || []).map(item => normalizeKeys<RoleOData>(item as unknown as Record<string, unknown>)),
        '@odata.count': data['@odata.count']
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  async getRole(id: string): Promise<RoleOData> {
    try {
      const response = await api.get<RoleOData>(`/v1/roles/${id}`)
      return normalizeKeys<RoleOData>(response.data as unknown as Record<string, unknown>)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}

// Re-export for backwards compatibility
export type Role = RoleOData
